// Single source of truth for catalog types + all rental price math.
// Used by product cards, the cart, checkout and the booking summary.

export interface CatalogPlan {
  id: number;
  name: string;
  days: number;
  price: number;
  label: string | null;
  tagline: string | null;
  product_id?: number | null;
}

export interface CatalogProduct {
  id: number;
  slug: string;
  name: string;
  short_description: string | null;
  features: string[];
  image_url: string | null;
  badge: string | null;
  plans: CatalogPlan[];
}

export interface CartItem {
  key: string;
  /** "rental" consumes a headset from inventory; "addon" is an accessory sold outright. */
  kind?: "rental" | "addon";
  productId: number;
  productSlug: string;
  productName: string;
  productImage: string | null;
  planId: number;
  planName: string;
  planLabel: string;
  planDays: number;
  unitPrice: number;
  quantity: number;
}

/** Accessory / add-on sold alongside a rental (link cable, strap …). */
export interface Addon {
  id: number;
  slug: string;
  name: string;
  short_description: string | null;
  features: string[];
  image_url: string | null;
  price: number;
  badge: string | null;
}

export const GST_RATE = 0.18;
export const MAX_QUANTITY_PER_ITEM = 5;
export const CART_STORAGE_KEY = "vrrentals.cart.v1";

export const cartItemKey = (productSlug: string, planId: number) => `${productSlug}::${planId}`;
export const addonItemKey = (slug: string) => `addon::${slug}`;

export const isAddonItem = (item: CartItem) => item.kind === "addon";

export const makeCartItem = (
  product: CatalogProduct,
  plan: CatalogPlan,
  quantity = 1,
): CartItem => ({
  key: cartItemKey(product.slug, plan.id),
  kind: "rental",
  productId: product.id,
  productSlug: product.slug,
  productName: product.name,
  productImage: product.image_url ?? null,
  planId: plan.id,
  planName: plan.name,
  planLabel: plan.label || plan.name,
  planDays: plan.days,
  unitPrice: plan.price,
  quantity,
});

export const makeAddonCartItem = (addon: Addon, quantity = 1): CartItem => ({
  key: addonItemKey(addon.slug),
  kind: "addon",
  productId: addon.id,
  productSlug: addon.slug,
  productName: addon.name,
  productImage: addon.image_url ?? null,
  planId: 0,
  planName: "Add-on",
  planLabel: "Add-on",
  planDays: 0,
  unitPrice: addon.price,
  quantity,
});

export const isValidCartItem = (item: unknown): item is CartItem => {
  if (!item || typeof item !== "object") return false;
  const i = item as Record<string, unknown>;
  const isAddon = i.kind === "addon";
  return (
    typeof i.key === "string" &&
    typeof i.productSlug === "string" &&
    typeof i.productName === "string" &&
    typeof i.planId === "number" &&
    typeof i.planDays === "number" &&
    (isAddon ? (i.planDays as number) >= 0 : (i.planDays as number) > 0) &&
    typeof i.unitPrice === "number" &&
    Number.isFinite(i.unitPrice as number) &&
    (i.unitPrice as number) >= 0 &&
    Number.isInteger(i.quantity) &&
    (i.quantity as number) >= 1
  );
};

export const lineTotal = (item: CartItem) => item.unitPrice * item.quantity;

export interface CartTotals {
  subtotal: number;
  gst: number;
  total: number;
  /** Headsets that must be reserved from inventory (add-ons excluded). */
  units: number;
  addonUnits: number;
  lines: number;
  maxDays: number;
}

export const cartTotals = (items: CartItem[]): CartTotals => {
  const subtotal = items.reduce((sum, i) => sum + lineTotal(i), 0);
  const gst = Math.round(subtotal * GST_RATE * 100) / 100;
  const rentals = items.filter((i) => !isAddonItem(i));
  const addons = items.filter(isAddonItem);
  return {
    subtotal,
    gst,
    total: subtotal + gst,
    units: rentals.reduce((sum, i) => sum + i.quantity, 0),
    addonUnits: addons.reduce((sum, i) => sum + i.quantity, 0),
    lines: items.length,
    maxDays: rentals.reduce((max, i) => Math.max(max, i.planDays), 0),
  };
};

export const formatINR = (amount: number) =>
  `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export interface CartValidation {
  ok: boolean;
  message?: string;
}

export const validateCartForCheckout = (items: CartItem[]): CartValidation => {
  if (items.length === 0) return { ok: false, message: "Your cart is empty." };
  if (items.some((i) => !isValidCartItem(i)))
    return { ok: false, message: "Some cart items are invalid. Please remove and add them again." };
  if (items.some((i) => i.quantity < 1 || i.quantity > MAX_QUANTITY_PER_ITEM))
    return { ok: false, message: `Quantity must be between 1 and ${MAX_QUANTITY_PER_ITEM} per plan.` };
  if (!items.some((i) => !isAddonItem(i)))
    return { ok: false, message: "Add a VR headset rental before checking out." };
  if (cartTotals(items).subtotal <= 0)
    return { ok: false, message: "Pricing information is missing for one of your items." };
  return { ok: true };
};

// Payload sent to create-booking for a cart-based order.
export const toBookingItems = (items: CartItem[]) =>
  items.map((i) => ({
    type: isAddonItem(i) ? "addon" : "rental",
    product_name: i.productName,
    plan_label: i.planLabel,
    plan_days: i.planDays,
    quantity: i.quantity,
    unit_price: i.unitPrice,
  }));
